import { User, LoginCredentials, AuthResponse } from '../types/auth'
import { getRoleCategory, UserRole } from '../types/user'
import { cognitoService } from './cognitoService'
import { getTenantId } from '../utils/tenant'
import { DEFAULT_ROLE_PERMISSIONS } from '../types/permissionTemplates'
import { rolePermissionService } from './rolePermissionService'
import { viteEnv } from '../utils/viteEnv'

// Check if Cognito is configured
const USE_COGNITO = !!(viteEnv.VITE_COGNITO_USER_POOL_ID && viteEnv.VITE_COGNITO_CLIENT_ID)

// Test users - in production, these would come from IAM/Cognito
// Used only when Cognito is not configured
const TEST_USERS: Array<User & { password: string }> = [
  {
    id: '1',
    email: 'admin@clnicl.ie',
    password: 'admin123',
    firstName: 'Admin',
    lastName: 'User',
    role: 'admin',
    roleCategory: 'Administrative',
    permissions: ['*'], // All permissions
  },
  {
    id: '2',
    email: 'doctor@clnicl.ie',
    password: 'doctor123',
    firstName: 'Dr. Sarah',
    lastName: 'O\'Brien',
    role: 'doctor',
    roleCategory: 'Clinical',
    permissions: ['patients:read', 'patients:write', 'appointments:read', 'appointments:write', 'templates:read', 'templates:write'],
  },
  {
    id: '3',
    email: 'nurse@clnicl.ie',
    password: 'nurse123',
    firstName: 'Nurse',
    lastName: 'Murphy',
    role: 'nurse',
    roleCategory: 'Clinical',
    permissions: ['patients:read', 'patients:write', 'appointments:read', 'appointments:write', 'templates:read', 'templates:write', 'lab-results:read', 'lab-results:write'],
  },
  {
    id: '4',
    email: 'reception@clnicl.ie',
    password: 'reception123',
    firstName: 'Reception',
    lastName: 'Staff',
    role: 'receptionist',
    roleCategory: 'Administrative',
    permissions: ['patients:read', 'patients:write', 'appointments:read', 'appointments:write', 'templates:read', 'templates:write'],
  },
]

const TOKEN_KEY = 'clnicl_auth_token'
const USER_KEY = 'clnicl_user'
const TOKEN_EXPIRY_KEY = 'clnicl_token_expiry'

export const authService = {
  /**
   * Login with email and password
   */
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    if (USE_COGNITO) {
      // Use Cognito authentication
      try {
        const tenantId = getTenantId()
        const authResult = await cognitoService.login(credentials)
        
        // Get user details from Cognito
        const cognitoUser = await cognitoService.getCurrentUser(authResult.accessToken)
        
        // Verify tenant ID matches current tenant (security check)
        if (tenantId && cognitoUser.tenantId) {
          if (cognitoUser.tenantId !== tenantId) {
            console.error('[Auth Service] Tenant ID mismatch:', {
              expectedTenantId: tenantId,
              userTenantId: cognitoUser.tenantId,
              hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
            })
            throw new Error(`User does not belong to this tenant. Expected: ${tenantId}, User has: ${cognitoUser.tenantId}`)
          }
          console.log('[Auth Service] Tenant ID verified:', tenantId)
        } else {
          console.warn('[Auth Service] Tenant ID check skipped:', {
            urlTenantId: tenantId || 'null',
            userTenantId: cognitoUser.tenantId || 'null',
          })
        }

        // Map Cognito user to our User type
        const userRole = (cognitoUser.role as UserRole) || 'receptionist'
        
        // Get permissions based on role (from role permissions system or defaults)
        // Fetch from role permissions API, fallback to defaults if API call fails
        let rolePermissions: string[] = []
        try {
          const rolePermission = await rolePermissionService.getByRole(userRole)
          rolePermissions = rolePermission.permissions || []
        } catch (error) {
          console.warn(`[Auth Service] Failed to fetch role permissions for ${userRole}, using defaults:`, error)
          rolePermissions = DEFAULT_ROLE_PERMISSIONS[userRole] || []
        }
        
        const user: User = {
          id: cognitoUser.username,
          // Expose Cognito username separately so users can log in
          // with either email or username depending on configuration.
          username: cognitoUser.username,
          email: cognitoUser.email,
          salutation: cognitoUser.salutation,
          firstName: cognitoUser.firstName,
          lastName: cognitoUser.lastName,
          role: userRole,
          roleCategory: getRoleCategory(userRole),
          permissions: rolePermissions,
        }

        const expiresAt = new Date(Date.now() + authResult.expiresIn * 1000).toISOString()

        const authResponse: AuthResponse = {
          user,
          token: authResult.idToken, // Use ID token for API calls
          expiresAt,
        }

        // Store in localStorage
        localStorage.setItem(TOKEN_KEY, authResult.idToken)
        localStorage.setItem(TOKEN_KEY + '_access', authResult.accessToken) // Store access token separately
        localStorage.setItem(TOKEN_KEY + '_refresh', authResult.refreshToken) // Store refresh token
        localStorage.setItem(USER_KEY, JSON.stringify(user))
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt)

        // Record last login (await so Settings > Users shows updated time; catch so login still succeeds on failure)
        try {
          const { userService } = await import('./userService')
          await userService.recordLastLogin(user.id)
        } catch (err) {
          console.warn('Failed to record last login', err)
        }

        return authResponse
      } catch (error: any) {
        throw new Error(error.message || 'Authentication failed')
      }
    }

    // Fallback to mock authentication for development
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500))

    // Find user by email
    const user = TEST_USERS.find((u) => u.email.toLowerCase() === credentials.email.toLowerCase())

    if (!user || user.password !== credentials.password) {
      throw new Error('Invalid email or password')
    }

    // Get permissions based on role (from role permissions system or defaults)
    // Fetch from role permissions API, fallback to defaults if API call fails
    let rolePermissions: string[] = []
    try {
      const rolePermission = await rolePermissionService.getByRole(user.role)
      rolePermissions = rolePermission.permissions || []
    } catch (error) {
      console.warn(`[Auth Service] Failed to fetch role permissions for ${user.role}, using defaults:`, error)
      // Fallback to user's hardcoded permissions or defaults
      rolePermissions = user.permissions || DEFAULT_ROLE_PERMISSIONS[user.role] || []
    }

    // Generate token (in production, this would come from the server)
    const token = `test_token_${user.id}_${Date.now()}`
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours

    const { password, ...userWithoutPassword } = user

    const authResponse: AuthResponse = {
      user: {
        ...userWithoutPassword,
        permissions: rolePermissions,
      },
      token,
      expiresAt,
    }

    // Store in localStorage
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(userWithoutPassword))
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt)

    // Record last login in mock/API (await so Settings > Users shows updated time)
    try {
      const { userService } = await import('./userService')
      await userService.recordLastLogin(user.id)
    } catch (err) {
      console.warn('Failed to record last login', err)
    }

    return authResponse
  },

  /**
   * Logout
   * Clears local storage immediately (synchronously) and invalidates Cognito session (asynchronously)
   */
  logout: async (): Promise<void> => {
    // Clear localStorage immediately to invalidate session synchronously
    const accessToken = localStorage.getItem(TOKEN_KEY + '_access')
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(TOKEN_KEY + '_access')
    localStorage.removeItem(TOKEN_KEY + '_refresh')
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_KEY)

    // Invalidate Cognito session asynchronously (fire and forget for synchronous callers)
    if (USE_COGNITO && accessToken) {
      try {
        await cognitoService.logout(accessToken)
      } catch (error) {
        console.error('Error during Cognito logout:', error)
        // Local storage already cleared, so session is invalidated locally
      }
    }
  },

  /**
   * Get current user from storage
   */
  getCurrentUser: (): User | null => {
    const userStr = localStorage.getItem(USER_KEY)
    const token = localStorage.getItem(TOKEN_KEY)
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY)

    if (!userStr || !token || !expiry) {
      return null
    }

    // Check if token is expired
    if (new Date(expiry) < new Date()) {
      authService.logout()
      return null
    }

    try {
      return JSON.parse(userStr)
    } catch {
      return null
    }
  },

  /**
   * Get current token
   */
  getToken: (): string | null => {
    const token = localStorage.getItem(TOKEN_KEY)
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY)

    if (!token || !expiry) {
      return null
    }

    // Check if token is expired
    if (new Date(expiry) < new Date()) {
      authService.logout()
      return null
    }

    return token
  },

  /**
   * Check if user has permission
   */
  hasPermission: (permission: string): boolean => {
    const user = authService.getCurrentUser()
    if (!user) return false

    // Admin has all permissions
    if (user.permissions?.includes('*')) return true

    return user.permissions?.includes(permission) || false
  },

  /**
   * Check if user has field-level permission
   * Supports field-level permissions like 'patients:read:medications'
   * 
   * For sensitive fields (medications, conditions, sensitive-documents), explicit
   * field-level permissions are required. 'patients:read' only grants basic patient info access.
   */
  hasFieldPermission: (field: string): boolean => {
    const user = authService.getCurrentUser()
    if (!user) return false

    // Admin has all permissions
    if (user.permissions?.includes('*')) return true

    const permissions = user.permissions || []
    
    // Sensitive fields require explicit permissions
    const sensitiveFields = ['medications', 'conditions', 'sensitive-documents', 'vital-signs', 'lab-results', 'allergies', 'family-history']
    const isSensitiveField = sensitiveFields.includes(field)
    
    // Check for field-specific permission (e.g., 'patients:read:medications')
    if (permissions.includes(`patients:read:${field}`)) {
      return true
    }
    
    // Check for full access permission
    if (permissions.includes('patients:read:full-access')) {
      return true
    }
    
    // Backward compatibility: 'patients:read' grants access to basic patient info only
    // For sensitive fields, explicit permissions are required
    if (permissions.includes('patients:read') && !isSensitiveField) {
      return true
    }
    
    return false
  },

  /**
   * Check if user can override access restrictions in emergency situations
   */
  canOverrideAccess: (): boolean => {
    const user = authService.getCurrentUser()
    console.log('[authService.canOverrideAccess] Checking override permission, user:', user?.id, 'permissions:', user?.permissions)
    if (!user) {
      console.log('[authService.canOverrideAccess] No user found, returning false')
      return false
    }

    // Admin has all permissions
    if (user.permissions?.includes('*')) {
      console.log('[authService.canOverrideAccess] User is admin, returning true')
      return true
    }

    const hasPermission = user.permissions?.includes('patients:override:emergency') || false
    console.log('[authService.canOverrideAccess] Has patients:override:emergency permission:', hasPermission)
    return hasPermission
  },

  /**
   * Check if user has role
   */
  hasRole: (role: User['role']): boolean => {
    const user = authService.getCurrentUser()
    return user?.role === role
  },

  /**
   * Refresh permissions for the current user from the role permissions API
   * This is useful when role permissions are updated while a user is logged in
   */
  refreshPermissions: async (): Promise<void> => {
    const user = authService.getCurrentUser()
    if (!user) {
      return
    }

    try {
      const rolePermission = await rolePermissionService.getByRole(user.role)
      const updatedUser: User = {
        ...user,
        permissions: rolePermission.permissions || [],
      }
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser))
    } catch (error) {
      console.warn(`[Auth Service] Failed to refresh permissions for ${user.role}:`, error)
      // Don't throw - keep existing permissions if refresh fails
    }
  },
}

